import { isPastDueDate } from '../../shared/utils/overdue';
import { getDocumentDueDate } from '../documents/utils/documents.overview';
import { getDealObligations, isCancelledRecord, isSettled, normalizedStatus } from '../dashboard/utils/dashboard.obligations';

export const PARTY_ACTIVITY_METRICS = [
  { id: 'tasks', label: 'Con task in corso', tone: 'progress', iconKey: 'nav.tasks' },
  { id: 'deals', label: 'Con affari in corso', tone: 'progress', iconKey: 'nav.dealsActive' },
  { id: 'overdue', label: 'Con elementi scaduti', tone: 'attention', iconKey: 'metrics.overdue' },
  { id: 'idle', label: 'Senza attività', tone: 'neutral', iconKey: 'metrics.requests' },
];

const key = (value) => value == null ? '' : String(value).trim();
const nameKey = (value) => key(value).toLocaleLowerCase('it');
const finished = new Set(['completato', 'completata', 'completed', 'done', 'chiuso', 'chiusa']);
const isOpen = (row) => !isCancelledRecord(row) && !finished.has(normalizedStatus(row.status));
const isOpenTask = (row) => isOpen(row) && !row.completedAt;
const typeAliases = {
  CLIENT: 'client', SUPPLIER: 'supplier', STAFF: 'staff', TEAM_MEMBER: 'staff',
  TASK: 'assignment', ASSIGNMENT: 'assignment', PROPOSAL: 'proposal', DEAL: 'inProgressDeal',
  IN_PROGRESS_DEAL: 'inProgressDeal', COMPLETED_DEAL: 'completedDeal', DOCUMENT: 'document',
  INVOICE: 'invoice', ACCOUNTING_INVOICE: 'invoice', PAYMENT: 'payment', ACCOUNTING_PAYMENT: 'payment',
  EXPENSE: 'expense', ACCOUNTING_EXPENSE: 'expense', CALENDAR_EVENT: 'calendarEvent',
};
const normalizeType = (type) => typeAliases[key(type).toUpperCase()] || type;
const collections = {
  assignments: 'assignment', dealProposals: 'proposal', dealsInProgress: 'inProgressDeal',
  dealsCompleted: 'completedDeal', payments: 'payment', expenses: 'expense',
  invoices: 'invoice', documents: 'document', calendarEvents: 'calendarEvent',
};

// Resolve explicit links first. Legacy labels are usable only when they identify
// exactly one party; account IDs and staff IDs are deliberately separate namespaces.
export function createPartyResolver(kind, parties, data) {
  const knownIds = new Set(parties.map((party) => key(party.id)).filter(Boolean));
  const names = new Map(), workspaceUsers = new Map(), records = new Map();
  const addIndex = (index, label, id) => {
    if (!label) return;
    if (!index.has(label)) index.set(label, new Set());
    index.get(label).add(id);
  };
  parties.forEach((party) => {
    const id = key(party.id);
    [party.name, party.fullName, party.company, party.businessName, party.displayName, party.legalName]
      .forEach((name) => addIndex(names, nameKey(name), id));
    if (kind === 'staff') addIndex(workspaceUsers, key(party.linkedWorkspaceUserId), id);
  });
  Object.entries(collections).forEach(([collection, type]) => {
    records.set(type, new Map((data[collection] || []).map((record) => [key(record.id), record])));
  });
  const legacyIds = (values) => values.flatMap((value) => key(value).split(';'))
    .flatMap((value) => names.get(nameKey(value))?.size === 1 ? [...names.get(nameKey(value))] : []);

  function resolve(row, type, visited = new Set()) {
    if (!row || visited.has(row)) return new Set();
    const seen = new Set(visited).add(row);
    const array = row[`${kind}Ids`];
    const single = row[`${kind}Id`] ?? (kind === 'staff' ? row.assigneeStaffId ?? row.assignedStaffId : null);
    const direct = Array.isArray(array) ? array : [single];
    const relations = Array.isArray(row.relations) ? row.relations : [];
    const partyRelations = relations.filter((relation) => normalizeType(relation.entityType) === kind);
    const ids = new Set([...direct, ...partyRelations.map((relation) => relation.entityId)]
      .map(key).filter((id) => knownIds.has(id)));
    let structured = Array.isArray(array) || Boolean(key(single)) || partyRelations.length > 0;
    if (kind === 'staff' && type === 'assignment') {
      const recipients = row.recipientIds || [];
      recipients.forEach((id) => workspaceUsers.get(key(id))?.forEach((staffId) => ids.add(staffId)));
      structured ||= recipients.length > 0;
    }
    if (kind === 'staff' && type === 'calendarEvent') {
      (row.attendees || row.attendeeIds || []).forEach((id) => {
        if (knownIds.has(key(id))) ids.add(key(id));
        else legacyIds([id]).forEach((staffId) => ids.add(staffId));
      });
    }
    if (kind === 'staff' && ['proposal', 'inProgressDeal', 'completedDeal'].includes(type) && !Array.isArray(array)) {
      legacyIds(row.teamCoinvolto || []).forEach((id) => ids.add(id));
    }
    if (!structured) {
      const labels = kind === 'client' ? [row.client]
        : kind === 'supplier' ? [row.supplierName]
          : type === 'assignment' ? row.recipients || [] : [row.assignee, ...(row.teamCoinvolto || [])];
      legacyIds(labels).forEach((id) => ids.add(id));
    }
    const links = [...relations];
    if (row.linkedEntityType && key(row.linkedEntityId)) links.push({ entityType: row.linkedEntityType, entityId: row.linkedEntityId });
    for (const linkedType of ['assignment', 'proposal', 'inProgressDeal', 'completedDeal', 'invoice', 'payment', 'expense']) {
      if (key(row[`${linkedType}Id`])) links.push({ entityType: linkedType, entityId: row[`${linkedType}Id`] });
    }
    links.forEach((link) => {
      const linkedType = normalizeType(link.entityType);
      if (linkedType === kind && knownIds.has(key(link.entityId))) ids.add(key(link.entityId));
      const linked = records.get(linkedType)?.get(key(link.entityId));
      resolve(linked, linkedType, seen).forEach((id) => ids.add(id));
    });
    return ids;
  }
  return resolve;
}

export function buildPartyActivity({ kind, parties = [], data = {}, now = new Date() }) {
  const result = { tasks: new Set(), deals: new Set(), overdue: new Set(), idle: new Set() };
  const openTasks = new Set();
  const resolve = createPartyResolver(kind, parties, data);
  const mark = (set, row, type) => resolve(row, type).forEach((id) => set.add(id));
  (data.assignments || []).filter(isOpenTask).forEach((task) => {
    mark(openTasks, task, 'assignment');
    if (['in_corso', 'in_progress'].includes(normalizedStatus(task.status))) mark(result.tasks, task, 'assignment');
    if (isPastDueDate(task.dueDate, now)) mark(result.overdue, task, 'assignment');
  });
  const completedSources = new Set((data.dealsCompleted || []).map((deal) => key(deal.sourceInProgressDealId)).filter(Boolean));
  (data.dealsInProgress || []).filter((deal) => isOpen(deal) && !deal.completedDealId && !deal.revertedAt && !completedSources.has(key(deal.id)))
    .forEach((deal) => mark(result.deals, deal, 'inProgressDeal'));
  getDealObligations(data).filter((item) => isPastDueDate(item.dueDate, now))
    .forEach((item) => mark(result.overdue, item.raw, item.entityType));
  for (const [collection, type] of [['payments', 'payment'], ['expenses', 'expense']]) {
    (data[collection] || []).filter((row) => isOpen(row) && !isSettled(row.status) && isPastDueDate(row.dueDate, now))
      .forEach((row) => mark(result.overdue, row, type));
  }
  (data.documents || []).filter((row) => isOpen(row) && isPastDueDate(getDocumentDueDate(row), now))
    .forEach((row) => mark(result.overdue, row, 'document'));
  (data.calendarEvents || []).filter((row) => !row.isReadOnly && row.source !== 'derived' && isOpen(row) && !isSettled(row.status)
    && ['deadline', 'scadenza', 'training', 'reminder', 'promemoria', 'incasso', 'pagamento'].includes(normalizedStatus(row.type))
    && isPastDueDate(row.date || row.startsAt, now))
    .forEach((row) => mark(result.overdue, row, 'calendarEvent'));
  parties.forEach((party) => {
    const id = key(party.id);
    if (id && !openTasks.has(id) && !result.deals.has(id) && !result.overdue.has(id)) result.idle.add(id);
  });
  return result;
}
